// @ts-nocheck

import { Component } from "@theme/component";
import { ThemeEvents, QuantitySelectorUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { onAnimationEnd } from "@theme/utilities";

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar with RXC support.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    "stickyBar",
    "addToCartButton",
    "quantityDisplay",
    "quantityNumber",
  ];

  /** @type {IntersectionObserver | null} */
  #stickyPanelIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #stickyFooterIntersectionObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #isRxcMode = false;

  /** FIX 1: Track RXC availability state */
  /** @type {boolean} */
  #rxcAvailable = false;

  /** FIX 1: Initialize from data attribute */
  /** @type {boolean} */
  #isRoxable = false;

  /** FIX 1: Track if RXC check is in progress */
  /** @type {boolean} */
  #rxcLoading = true;

  #visibleFooterGroups = new Set();

  connectedCallback() {
    super.connectedCallback();

    // FIX 1: Initialize #isRoxable from data attribute FIRST
    this.#isRoxable = this.dataset.isRoxable === "true";

    const scrollTopBtn = this.querySelector(".sticky-add-to-cart__scroll-top");
    if (scrollTopBtn) {
      scrollTopBtn.addEventListener("click", () => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    }

    this.#setupIntersectionObserver();

    const { addToCartButton } = this.refs;
    if (addToCartButton) {
      addToCartButton.addEventListener("click", () =>
        this.handleAddToCartClick(),
      );
    }

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section");
    document?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
    document?.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected,
      { signal },
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartAddComplete,
      { signal },
    );
    document.addEventListener(
      ThemeEvents.cartError,
      this.#handleCartAddComplete,
      { signal },
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal },
    ); // FIX 2: Add listener for RXC availability event

    // FIX 2: Add listener for RXC availability event
    document.addEventListener("select-lenses", this.#handleSelectLensesEvent, {
      signal,
    });

    document.addEventListener("rxc-unavailable", this.#handleRxcUnavailable, {
      signal,
    });

    // 🔥 Ascolta i cambiamenti di variante dal size selector
    document.addEventListener(
      "variant:change",
      (event) => {
        const variantId = event.detail?.variantId;
        if (!variantId) return;

        // Usa i dati dal custom event
        const variantData = event.detail?.dataset;
        if (!variantData) return;

        // Aggiorna il dataset con i dati della variante corrente
        Object.assign(this.dataset, {
          currentVariantId: variantId,
          variantAvailable: variantData.available ? "true" : "false",
          stock: Number(variantData.stock ?? 0),
          inventoryQuantity: Number(variantData.inventoryQuantity ?? 0),
          inventoryPolicy: variantData.inventoryPolicy ?? "deny",
          hasEverStock:
            variantData.hasEverStock === true ||
            variantData.hasEverStock === "true"
              ? "true"
              : "false",
          isPreorder:
            variantData.isPreorder === true || variantData.isPreorder === "true"
              ? "true"
              : "false",
          optionValueId: variantData.optionValueId,
          expectedSaleDate: variantData.expectedSaleDate,
        });

        // Reset RXC state on variant change
        /* this.#rxcLoading = true;
        this.#rxcAvailable = false; */
        this.#updateButtonState();
      },
      { signal },
    );

    this.#getInitialQuantity();

    // FIX 1: Now call updateButtonState AFTER isRoxable is initialized
    this.#updateButtonState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stickyPanelIntersectionObserver?.disconnect();
    this.#stickyFooterIntersectionObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest(".buy-buttons-block");
    if (!buyButtonsBlock) return;

    const isMobile = window.matchMedia("(max-width: 1023px)").matches;
    const productDetails = document.querySelector(".product-details");

    //const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    //if (!footer) return;
    const footerGroups = document.querySelectorAll('[class*="footer-group"]');
    if (!footerGroups) return;

    const bar = this.refs.stickyBar;
    if (!bar) return;

    // Observer for buy buttons visibility
    this.#stickyPanelIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        // Only show sticky bar if buy buttons have been scrolled past (above viewport)
        if (!entry.isIntersecting && !this.#isStuck) {
          // Check if the element is above the viewport (scrolled past) or below (not yet reached)
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            // Element is above viewport - show sticky bar
            this.#showStickyBar();
            this.#setActiveState(true);
          }
          // If rect.top >= 0, element is below viewport - don't show sticky bar yet
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
          this.#setActiveState(false);
        }
      },
    );

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#stickyFooterIntersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#visibleFooterGroups.add(entry.target);
          } else {
            this.#visibleFooterGroups.delete(entry.target);
          }
        });

        const anyVisible = this.#visibleFooterGroups.size > 0;

        if (anyVisible) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
          this.#setActiveState(false);
        } else if (this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();

          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
            this.#setActiveState(true);
          }
        }
      },
      {
        rootMargin: "200px 0px 0px 0px",
      },
    );

    if (isMobile) {
      this.#showStickyBar();
    }

    if (!isMobile && productDetails) {
      this.#stickyPanelIntersectionObserver.observe(productDetails);
    }
    //this.#stickyFooterIntersectionObserver.observe(footer);
    footerGroups.forEach((group) => {
      this.#stickyFooterIntersectionObserver.observe(group);
    });
    this.#targetAddToCartButton = productForm.querySelector(
      '[ref="addToCartButton"]',
    );
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const action = this.refs.addToCartButton?.dataset.action || "add-to-cart";

    // Route to appropriate handler based on action
    if (action === "select-lenses") {
      // Trigger RXC activator button click
      const rxcButton = document.querySelector(
        'rxc-activator button[name="rxc-activator"]',
      );
      if (rxcButton) {
        rxcButton.click();
      }
      return;
    }

    // Default: Add to Cart behavior
    if (!this.#targetAddToCartButton) return;

    const form = this.#getProductForm();
    const variantInput = form?.querySelector('[name="id"]');

    if (variantInput && this.dataset.currentVariantId) {
      variantInput.value = this.dataset.currentVariantId;

      // forza Shopify a ricalcolare stato form
      variantInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    //this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    if (this.refs.addToCartButton.dataset.added !== "true") {
      this.refs.addToCartButton.dataset.added = "true";
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage)
      return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add("fly-to-cart--sticky");
    flyToCartElement.style.setProperty(
      "background-image",
      `url(${this.refs.productImage.src})`,
    );
    flyToCartElement.useSourceSize = "true";
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute("data-added");
    }, 800);
  };

  #handleRxcUnavailable = () => {
    if (!this.#isRoxable) return;

    this.#rxcAvailable = false;
    this.#rxcLoading = false;

    this.#updateButtonState();
  };

  /**
   * FIX 2: Handles RXC select-lenses event from RXCActivator
   * This is called when RXCActivator detects RXC is available
   * @param {CustomEvent} event - The select-lenses event
   */
  #handleSelectLensesEvent = (event) => {
    // Only process if product is roxable
    if (!this.#isRoxable) {
      return;
    }

    // Update RXC state
    this.#rxcAvailable = true;
    this.#rxcLoading = false;

    // Update button to show "Select Lenses"
    this.#updateButtonState();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  /* #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart =
      event.detail.data.html.querySelector("sticky-add-to-cart");
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck =
      this.refs.stickyBar.getAttribute("data-stuck") || "false";
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute("data-stuck", currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector(
        '[ref="addToCartButton"]',
      );
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    // Reset RXC state on variant change
    this.#rxcLoading = true;
    this.#rxcAvailable = false;

    // Restore the current quantity display if needed
    this.#updateButtonText();

    // Update button state to show loading spinner
    this.#updateButtonState();
  }; */

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
      this.dataset.variantAvailable = variant.available ? "true" : "false";

      // UPDATE: Aggiorna il titolo della variante nello sticky bar
      const variantTitleElement = this.querySelector(
        ".sticky-add-to-cart__variant",
      );
      if (variantTitleElement && variant.title) {
        variantTitleElement.textContent = variant.title;
      }

      // UPDATE: Aggiorna il prezzo nello sticky bar
      const stickyPriceContainer = this.querySelector(
        ".sticky-add-to-cart__price",
      );
      if (!stickyPriceContainer || !variant) return;

      const priceElement = stickyPriceContainer.querySelector(".price");
      const compareAtPriceElement =
        stickyPriceContainer.querySelector(".compare-at-price");

      if (priceElement) {
        priceElement.textContent = variant.price
          ? `$${(variant.price / 100).toFixed(2)}`
          : "";
      }
      if (compareAtPriceElement) {
        compareAtPriceElement.textContent = variant.compare_at_price
          ? `$${(variant.compare_at_price / 100).toFixed(2)}`
          : "";
      }
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector(
        '[ref="addToCartButton"]',
      );
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    // Reset RXC state on variant change
    /* this.#rxcLoading = true;
    this.#rxcAvailable = false; */

    // Restore the current quantity display if needed
    this.#updateButtonText();

    // Update button state to show loading spinner
    this.#updateButtonState();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(
      ".sticky-add-to-cart__variant",
    );
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`,
    );
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll("input:checked"),
    )
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== "")
      .join(" / ");
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = "false";
      this.redirectToCart();
    }
  };

  redirectToCart() {
    //const cartUrl = window.routes.cart_url || `${window.Shopify?.routes?.root || "/"}cart`;
    const root = window.Shopify?.routes?.root || "/";
    const cartUrl = `${root}cart`;

    const separator = cartUrl.includes("?") ? "&" : "?";
    window.location.href = `${cartUrl}?disable_event=true`;
  }

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #setActiveState(isActive) {
    this.classList.toggle("is-active", isActive);
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = "true";
    this.#setActiveState(true);
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = "false";
    this.#setActiveState(false);
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace("shopify-section-", "");
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`,
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || "1") || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = "inline";
    } else {
      quantityDisplay.style.display = "none";
    }
  }

  /**
   * Updates button state based on RXC availability and roxable flag
   *
   * Logic:
   * 1. If not roxable: Always show "Add to Cart" (no RXC check needed)
   * 2. If roxable + RXC loading: Show spinner
   * 3. If roxable + RXC available: Show "Select Lenses"
   * 4. If roxable + RXC not available: Show "Add to Cart" (fallback)
   * 5. If variant unavailable or stock = 0: Disable button
   */
  #updateButtonState() {
    const { addToCartButton } = this.refs;
    const selectText = addToCartButton.dataset.selectLensesText;
    const addText = addToCartButton.dataset.addToCartText;
    if (!addToCartButton) return;

    const buttonTextSpan = addToCartButton.querySelector(
      ".add-to-cart-text__content span:first-child",
    );
    const spinnerContainer = addToCartButton.querySelector(
      '[data-testid="sticky-button-spinner"]',
    );
    const variantAvailable = this.dataset.variantAvailable === "true";
    const stock = Number(this.dataset.stock ?? 0);
    const hasEverStock = this.dataset.hasEverStock === "true";
    const inventoryPolicy = this.dataset.inventoryPolicy;
    const isPreorder = this.dataset.isPreorder === "true";
    const allowPreorder =
      stock <= 0 &&
      hasEverStock != true &&
      inventoryPolicy === "continue" &&
      isPreorder;

    const market = this.dataset.market;

    /* console.log(
      "variantAvailable", variantAvailable,
      "inventoryQuantity", this.dataset.inventoryQuantity,
      "stock", stock,
      "hasEverStock", hasEverStock,
      "inventoryPolicy", inventoryPolicy,
      "isPreorder", isPreorder,
      "allowPreorder", allowPreorder
    ); */

    const hideStickyBar = market === "AU" &&  stock <= 0 &&  !allowPreorder;
 
    if (hideStickyBar) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    // Check if variant is available (stock > 0)
    if (stock <= 0 && !allowPreorder) {
      addToCartButton.hidden = true;
      this.refs.stickyBar.style.display = "none !important";
      if (spinnerContainer) spinnerContainer.style.display = "none";
      if (buttonTextSpan) {
        buttonTextSpan.style.display = "inline";
        buttonTextSpan.textContent = addText;
      }
      return;
    }
    addToCartButton.hidden = false;
    this.refs.stickyBar.style.display = "flex";

    // If NOT roxable: Skip RXC check entirely, show "Add to Cart"
    if (!this.#isRoxable) {
      addToCartButton.disabled = false;
      if (spinnerContainer) spinnerContainer.style.display = "none";
      if (buttonTextSpan) {
        buttonTextSpan.style.display = "inline";
        buttonTextSpan.textContent = addText;
      }
      addToCartButton.dataset.action = "add-to-cart";

      //check if the id of the parent <sticky-add-to-cart>
      const parentStickyAddToCart =
        addToCartButton.closest("sticky-add-to-cart");
      if (parentStickyAddToCart.id == "stickyBar") {
        addToCartButton.dataset.elementId = "Stickybar_AddCart";
      } else {
        //stickyPanel
        addToCartButton.dataset.elementId = "Prod_AddCart";
      }
      addToCartButton.dataset.redirect = "hiddenaction";
      return;
    }

    // ROXABLE PRODUCT: Handle RXC states
    // State 1: RXC Loading (show spinner, disable button)
    if (this.#rxcLoading) {
      addToCartButton.disabled = true;
      if (spinnerContainer) spinnerContainer.style.display = "inline-block";
      if (buttonTextSpan) buttonTextSpan.style.display = "none";
      return;
    }

    // State 2: RXC Loaded - show appropriate button
    if (spinnerContainer) spinnerContainer.style.display = "none";
    if (buttonTextSpan) buttonTextSpan.style.display = "inline";

    // State 3: RXC Available + Roxable = "Select Lenses"
    if (this.#rxcAvailable) {
      addToCartButton.disabled = false;
      if (buttonTextSpan) buttonTextSpan.textContent = selectText;
      addToCartButton.dataset.action = "select-lenses";

      //check if the id of the parent <sticky-add-to-cart>
      const parentStickyAddToCart =
        addToCartButton.closest("sticky-add-to-cart");
      if (parentStickyAddToCart.id == "stickyBar") {
        addToCartButton.dataset.elementId = "Stickybar_AddLens";
      } else {
        //stickyPanel
        addToCartButton.dataset.elementId = "Prod_AddLens";
      }
      return;
    }

    // State 4: RXC Not Available (fallback) = "Add to Cart"
    addToCartButton.disabled = false;
    if (buttonTextSpan) buttonTextSpan.textContent = addText;
    addToCartButton.dataset.action = "add-to-cart";
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCartComponent);
}
